import { AttributeKinds } from '@/constants/attributeTypes';
import { useQuote } from '@/hooks/quote/use-quote-store';
import { type QuoteAttributeUpdate, useQuoteAttributeUpdates } from '@/hooks/quote/use-coverage';
import { ModalDialog } from './ModalDialog';
import { ButtonColors, ButtonSizes, ButtonTypes, ButtonVariants } from '@/constants/buttonTypes';
import { Typography } from '@mui/material';
import { ComponentButton } from './formElements/buttons/ComponentButton';
import useModal from '@/hooks/use-modals';
import { InertIcon } from './InertIcon';
import { IconColors, IconSizes } from '@/constants/iconTypes';
import { List } from './List';
import { ListColors } from '@/constants/listTypes';
import { Layout } from './Layout';
import { LayoutAlignItems, LayoutDirection, LayoutJustifyContent, LayoutPadding } from '@/constants/layoutTypes';
import { ModalLink } from './formElements/typographies/ModalLink';
import { LinkTypes } from '@/constants/linkTypes';
import {
  TypographyAlign,
  TypographyColors,
  TypographyFontWeight,
  TypographyVariants,
} from '@/constants/typographyTypes';
import useAnalytics from '@/hooks/use-analytics';

interface LienholderConfirmationModalProps {
  modal_id: string;
  coverage_symbols: string[];
}

export const LienholderConfirmationModal = ({ modal_id, coverage_symbols }: LienholderConfirmationModalProps) => {
  const { trackEvent } = useAnalytics();
  const { getModal } = useModal();
  const quote = useQuote();
  const applyQuoteAttributeUpdates = useQuoteAttributeUpdates();

  const coveragesToDecline = quote?.coverages.filter((coverage) => coverage_symbols.includes(coverage.symbol)) ?? [];

  function handleConfirm() {
    if (!quote || coveragesToDecline.length === 0) return;

    trackEvent('LIENHOLDER_CONFIRMATION_MODAL_CONFIRMED');
    updateDeclinedCoverages();
    closeModal();
  }

  function updateDeclinedCoverages() {
    const updates: QuoteAttributeUpdate[] = [];

    for (const coverage of coveragesToDecline) {
      const declinedAttribute = coverage.attributes.find((attr) => attr.kind === AttributeKinds.DECLINED);
      if (!declinedAttribute?.selectionOptions.length) continue;

      const targetOption = declinedAttribute.selectionOptions.find((opt) => {
        const value = opt.value as { declined?: boolean };
        return value.declined === true;
      });

      if (!targetOption) continue;

      updates.push({
        symbol: coverage.symbol,
        scope: coverage.scope,
        kind: AttributeKinds.DECLINED,
        value: targetOption.value,
      });
    }

    if (updates.length === 0) return;

    applyQuoteAttributeUpdates(updates);

    trackEvent('LIENHOLDER_CONFIRMATION_MODAL_UPDATE');
  }

  function closeModal() {
    const modal = getModal(modal_id);
    modal?.close();
  }

  const modalContent = (
    <>
      <Layout
        direction={LayoutDirection.COLUMN}
        spacing={4}
        align_items={LayoutAlignItems.CENTER}
        justify_content={LayoutJustifyContent.CENTER}
        padding={LayoutPadding.SMALL}
      >
        <InertIcon
          icon_name='report'
          size={IconSizes.LARGE}
          color={IconColors.PRIMARY}
          filled
        />
        <Typography variant='h2'>Are you sure?</Typography>
      </Layout>
      <Typography
        variant='h3'
        textAlign='center'
      >
        Earlier you indicated you were financing or leasing a vehicle.
      </Typography>
      <Typography variant='body1'>
        Comprehensive and collision coverages are <b>required</b> by your financing / leasing company and removing them
        could put your contract at risk.
      </Typography>
      <Typography variant='body1'>
        Without these coverages, you are at risk for the <b>total loan amount</b> in events such as:
      </Typography>
      <List
        color={ListColors.PRIMARY}
        listType='unordered'
      >
        <Typography variant='body1'>If your car is stolen or vandalized</Typography>
        <Typography variant='body1'>
          If you have an accident with another vehicle or object and you are at fault
        </Typography>
        <Typography variant='body1'>If your car is damaged due to a storm</Typography>
      </List>
    </>
  );

  const footerContent = (
    <>
      <ComponentButton
        component={{
          id: `coverage_submit_button_remove`,
          label: 'Yes, remove coverages',
          size: ButtonSizes.LARGE,
          color: ButtonColors.PRIMARY,
          start_icon: null,
          variant: ButtonVariants.CONTAINED,
          type: ButtonTypes.MODAL,
          modal_id: modal_id,
          method_type: 'close',
          end_icon: null,
        }}
        onClick={handleConfirm}
      />
      <ModalLink
        component={{
          type: LinkTypes.MODAL,
          text: 'No, keep coverages',
          method_type: 'close',
          modal_id: modal_id,
          color: TypographyColors.PRIMARY,
          align: TypographyAlign.CENTER,
          variant: TypographyVariants.BODY1,
          font_weight: TypographyFontWeight.MEDIUM,
        }}
      />
    </>
  );

  return (
    <ModalDialog
      id={modal_id}
      status={'closed'}
      children={modalContent}
      footerChildren={footerContent}
    />
  );
};
